import React, { useState } from 'react';
import { AccordionDetails, Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useFormikContext } from 'formik';
import {
  AccountData,
  PassOut,
  PassStatus,
  PaymentEntityType,
  RefundablePaymentData,
  RefundOwnerType,
} from '../../../../api';
import { Accordion, AccordionSummary } from '../../../../calendar/dialogs/EditOrderDialog/components/Accordion';
import { FormTextField } from '../../../../experienceSettings/formComponents/FormTextField';
import { useAuth } from '../../../../session/InternalAuthProvider';
import { getPassOrderPage } from '../../../../utils/AccountUtils';
import { UtmBlock } from '../../../../common/ui/UtmBlock';
import { getRecurringSubscriptionView } from '../../utils';
import { useLanguage } from '../../../../common/GeneralUtils';
import { PaymentRefundRow } from '../../../../refunds/PaymentRefundRow';
import { RefundDialog } from '../../../../refunds/RefundDialog';
import { findRefundItem, useRefundOverview } from '../../../../refunds/useRefundOverview';
import { EditPassFormValues, PassDetailPanels } from '../../PassDetailTypes';

type Props = {
  pass: PassOut;
  expandedPanel: PassDetailPanels | undefined;
  isRecurring: boolean;
  onPanelChange: (panel: PassDetailPanels) => (event: React.SyntheticEvent, isExpanded: boolean) => void;
};

export const ManagementPanel = ({ pass, expandedPanel, isRecurring, onPanelChange }: Props) => {
  const { t } = useTranslation();
  const language = useLanguage();
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  const passOrderPage = getPassOrderPage(account, pass.order_page_id);
  const subscriptionLabel = isRecurring ? getRecurringSubscriptionView(pass, passOrderPage, t).label : '';
  const statusLabel = subscriptionLabel || t(`passes.status.${pass.status}`);
  // Preview the comment as it is typed rather than the last saved value.
  const { values } = useFormikContext<EditPassFormValues>();
  const { overview, mutate } = useRefundOverview(isRecurring ? undefined : RefundOwnerType.PASS, pass.id);
  const [refundItem, setRefundItem] = useState<RefundablePaymentData | undefined>(undefined);

  return (
    <Accordion
      expandedPanelName={expandedPanel}
      panelName={PassDetailPanels.Management}
      onChange={onPanelChange}
      allOpenValue={PassDetailPanels.AllOpen}>
      <AccordionSummary
        title={t('calendar.edit_order.magmntSection')}
        summaryComponent={
          values.comment ? (
            <Typography variant="body2" color="text.secondary" noWrap maxWidth="240px">
              {values.comment}
            </Typography>
          ) : undefined
        }
      />
      <AccordionDetails>
        <Grid container flexDirection="column" gap={2}>
          <FormTextField
            fieldName="comment"
            label={t('calendar.edit_event.comments')}
            minRows={2}
            isFieldUpdate
            width="100%"
          />

          {!isRecurring && pass.status !== PassStatus.ARCHIVED && (
            <Grid
              container
              gap={0.5}
              flexDirection="column"
              borderRadius="8px"
              border="1px solid"
              borderColor="divider"
              p={1.5}>
              <Typography fontWeight={700}>{t('calendar.edit_order.status')}</Typography>
              <Typography>{statusLabel}</Typography>
            </Grid>
          )}

          {!isRecurring && pass.status === PassStatus.ARCHIVED && (
            <Grid
              container
              gap={0.5}
              flexDirection="column"
              borderRadius="8px"
              border="1px solid"
              borderColor="divider"
              p={1.5}>
              <Typography fontWeight={700}>{t('calendar.edit_order.status')}</Typography>
              <Typography>{t('passes.status.archived')}</Typography>
            </Grid>
          )}

          {!isRecurring && (
            <PaymentRefundRow
              item={findRefundItem(overview, PaymentEntityType.PASS, pass.id)}
              onRefund={setRefundItem}
            />
          )}

          <UtmBlock utmData={pass.utm_data} />

          <Grid
            container
            gap={0.5}
            flexDirection="column"
            borderRadius="8px"
            border="1px solid"
            borderColor="divider"
            p={1.5}>
            <Typography fontWeight={700}>{t('passes.code')}</Typography>
            <Typography>{pass.code}</Typography>
          </Grid>
        </Grid>
        {!isRecurring && (
          <RefundDialog
            isOpen={!!refundItem}
            onClose={() => setRefundItem(undefined)}
            item={refundItem}
            ownerLabel={passOrderPage?.label[language] ?? t('refunds.entity.pass')}
            onRefunded={() => {
              void mutate();
            }}
          />
        )}
      </AccordionDetails>
    </Accordion>
  );
};
